import axios from 'axios';
import { Action } from 'redux';
import { IRequestAction } from 'redux/root';

import { enqueueSnackbar } from 'redux/snackbar/actions';
import { IUpdateUserInput, IUserEdge } from 'stubs/graphql/user';
import {
  publicURL,
  authenticatedURL,
  publicGQL,
  authenticatedGQL,
} from 'stubs';
import { showLoader, hideLoader } from 'redux/loader/actions';

// --------------
// Actions
// --------------
export enum ACCOUNT_ACTIONS {
  SET_LOGGED_IN = 'ACCOUNT_ACTIONS/SET_LOGGED_IN',
  SET_LOGGED_OUT = 'ACCOUNT_ACTIONS/SET_LOGGED_OUT',
  UPDATE_ME = 'ACCOUNT_ACTIONS/UPDATE_ME',
  RESET_STORE = 'ACCOUNT_ACTIONS/RESET_STORE',
}
// --------------
// Action types
// --------------
export type ISetLoggedIn = Action<ACCOUNT_ACTIONS.SET_LOGGED_IN>;

export type ISetLoggedOut = Action<ACCOUNT_ACTIONS.SET_LOGGED_OUT>;

export interface IUpdateMe extends Action<ACCOUNT_ACTIONS.UPDATE_ME> {
  me: IUserEdge;
}

export type IResetAccountStore = Action<ACCOUNT_ACTIONS.RESET_STORE>;

export type IAccountActions =
  | ISetLoggedIn
  | ISetLoggedOut
  | IUpdateMe
  | IResetAccountStore;

// --------------
// Action creators
// --------------

export const logInRequest =
  (username: string, password: string): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = publicGQL.logIn;
      const variables = { username, password };
      await axios.post(publicURL, { query, variables });

      // Fetch user's details
      await dispatch(meRequest());

      return null;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      dispatch(hideLoader());
    }
  };

export const logOutRequest = (): IRequestAction => async (dispatch) => {
  try {
    dispatch(showLoader());
    const query = authenticatedGQL.logOut;
    await axios.post(authenticatedURL, { query });

    // Resetting account store triggers redirection to login page
    dispatch(resetAccountStore());
  } catch {
    // Handled by interceptor
  } finally {
    dispatch(hideLoader());
  }
};

export const meRequest = (): IRequestAction => async (dispatch) => {
  try {
    const query = authenticatedGQL.me;
    const res = await axios.post<any>(authenticatedURL, {
      query,
    });

    const me: IUserEdge = res.data.data.me;

    dispatch(updateMe(me));
    dispatch(setLoggedIn());
  } catch {
    // Handled by interceptor
  }
};

export const updateMeRequest =
  (input: IUpdateUserInput): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = authenticatedGQL.updateMe;
      const variables = { input };
      const res = await axios.post<any>(authenticatedURL, { query, variables });

      const me: IUserEdge = res.data.data.updateMe;

      dispatch(
        enqueueSnackbar({
          message: `Successfully updated details`,
          options: {
            variant: 'success',
            autoHideDuration: 3000,
          },
        })
      );
      dispatch(updateMe(me));
    } catch (e) {
      // Handled by interceptor
    } finally {
      dispatch(hideLoader());
    }
  };

export const resetPasswordRequest =
  (code: string, password: string): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = publicGQL.resetPassword;
      const variables = { code, password };
      await axios.post(publicURL, { query, variables });

      return true;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      dispatch(hideLoader());
    }
  };

export const sendResetPasswordEmailRequest =
  (username: string, email: string): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = publicGQL.sendResetPasswordEmail;
      const variables = { username, email };
      await axios.post(publicURL, { query, variables });

      return true;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      dispatch(hideLoader());
    }
  };

export const setLoggedIn = (): ISetLoggedIn => ({
  type: ACCOUNT_ACTIONS.SET_LOGGED_IN,
});

export const setLoggedOut = (): ISetLoggedOut => ({
  type: ACCOUNT_ACTIONS.SET_LOGGED_OUT,
});

export const updateMe = (me: IUserEdge): IUpdateMe => ({
  type: ACCOUNT_ACTIONS.UPDATE_ME,
  me,
});

export const resetAccountStore = (): IResetAccountStore => ({
  type: ACCOUNT_ACTIONS.RESET_STORE,
});
